'use client'

import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLanguage } from '@/context/LanguageContext'
import { toast } from '@/components/ui/toast'

/**
 * Global working/display-language selector for the console (plan 03 §6.1).
 * EN by default; lists the nine supported languages. Sets the operator's
 * working language (persisted) — the default target for live content
 * translation across the app.
 */
export function WorkingLanguageSelector() {
  const { workingLanguage, setWorkingLanguage, languages } = useLanguage()
  const current = languages.find((l) => l.code === workingLanguage)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1.5',
            'border border-border/50 bg-white/50',
            'text-xs font-medium text-muted-foreground',
            'transition-all duration-200',
            'hover:border-brand-cornflower/30 hover:bg-brand-cornflower/10 hover:text-brand-navy',
            'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
          )}
          aria-label='Select working language'
        >
          <Icons.globe className='h-3.5 w-3.5' strokeWidth={1.5} />
          <span className='font-semibold'>{current?.label || 'EN'}</span>
          <Icons.chevronDown className='h-3 w-3' strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end' className='w-52'>
        <DropdownMenuLabel className='text-xs text-muted-foreground'>
          Working language
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => {
              setWorkingLanguage(lang.code)
              toast.success(`Working language: ${lang.name}`, {
                description: `Customer emails, help desk messages and invoice lines translate to ${lang.name} when you switch them from Original.`,
              })
            }}
            className='gap-2 rounded-lg px-3 py-2'
          >
            <span
              className={cn(
                'inline-flex h-6 w-7 items-center justify-center rounded',
                'text-[10px] font-semibold',
                lang.code === workingLanguage
                  ? 'bg-brand-cornflower/15 text-brand-navy'
                  : 'bg-muted/60 text-muted-foreground'
              )}
            >
              {lang.label}
            </span>
            <span className='flex-1 text-sm'>{lang.name}</span>
            {lang.code === workingLanguage && (
              <Icons.check className='h-4 w-4 text-brand-cornflower' strokeWidth={2} />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
